import { useEffect, useState } from "react";
import { StoredInternal } from "../../../types";
import { ApiError, ConfigApi, InternalApi } from "../api";
import { href } from "../route";
import { relativeTime } from "../format";
import { Button, Panel } from "./Shell";

// log-pile's own errors: a plain notification list, separate from the
// captures it exists to collect. Written by backend/utils/selfReport.ts.
export const InternalList = () => {
  const [items, setItems] = useState<StoredInternal[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [retentionDays, setRetentionDays] = useState<number | undefined>();

  const load = async (reset: boolean) => {
    setLoading(true);
    setError(undefined);
    try {
      const page = await InternalApi.list(reset ? undefined : cursor);
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
      setCursor(page.cursor);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(true);
    ConfigApi.get().then((c) => setRetentionDays(c.internalRetentionDays)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id: string) => {
    try {
      await InternalApi.delete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Delete failed");
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-500">
        Errors inside log-pile itself (ingest, API). Kept {retentionDays ?? "…"} days (<a className="underline" href={href.settings()}>Settings</a>). These are not captures from other services — see Groups and All captures for those.
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Panel>
        {items.map((i) => (
          <div key={i.id} className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <span className="font-mono">{i.where}</span>
              <span className="flex-1" />
              <span title={i.at}>{relativeTime(i.at)}</span>
              <button className="hover:underline" onClick={() => setOpen((s) => { const n = new Set(s); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })}>
                {open.has(i.id) ? "less" : "more"}
              </button>
              <button className="text-red-600 hover:underline" onClick={() => remove(i.id)}>delete</button>
            </div>
            <div className="text-sm"><span className="font-semibold">{i.name}:</span> {i.message}</div>
            {open.has(i.id) && (
              <div className="mt-1 space-y-1">
                {i.context && <pre className="text-xs bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded p-2 overflow-x-auto">{JSON.stringify(i.context, null, 2)}</pre>}
                {i.stack && <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words">{i.stack}</pre>}
              </div>
            )}
          </div>
        ))}
        {!loading && items.length === 0 && <p className="p-4 text-sm text-neutral-500">No internal errors. Good.</p>}
        <div className="p-2 flex items-center gap-2 text-xs text-neutral-500">
          <span>{items.length} shown</span>
          <span className="flex-1" />
          {cursor && <Button onClick={() => load(false)} disabled={loading}>{loading ? "Loading…" : "Load more"}</Button>}
        </div>
      </Panel>
    </div>
  );
};
